import React, { useState, useEffect } from 'react';
import {
  Box, Container, Heading, Text, VStack, HStack, Divider, Badge, Button,
  Alert, AlertIcon, Grid, GridItem, Image, Flex, useToast, Circle
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhoneIcon, CalendarIcon, EmailIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import axios from 'axios';
import Loader from '../components/Loader';
import { fetchOrderDetails, cancelOrder, verifyPayment } from '../services/orderService';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const data = await fetchOrderDetails(orderId);
      setOrder(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch order details');
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to fetch order details',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancelling(true);
    try {
      const response = await cancelOrder(orderId);
      setOrder(response.order);
      toast({
        title: 'Order Cancelled',
        description: 'Your order has been cancelled successfully',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Cancellation Failed',
        description: error.response?.data?.error || 'Failed to cancel order',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      paid: 'green',
      shipped: 'blue',
      delivered: 'green',
      failed: 'red',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const handlePayNow = async () => {
    setPaying(true);
    
    try {
      // Check if order has razorpayOrderId
      if (!order.razorpayOrderId) {
        throw new Error('Payment session not found. Please contact support.');
      }

      // Use existing Razorpay order ID from the pending order
      const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;

      const options = {
        key: razorpayKeyId,
        amount: order.amount * 100,
        currency: 'INR',
        name: 'Agnir',
        description: `Payment for Order #${order.orderId.substring(0, 13)}`,
        order_id: order.razorpayOrderId, // Use existing Razorpay order ID
        handler: async function (response) {
          try {
            const result = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.orderId
            });

            toast({
              title: 'Payment Successful!',
              description: 'Your payment has been processed successfully',
              status: 'success',
              duration: 5000,
              isClosable: true
            });

            // Update order with new data from verification response
            if (result.order) {
              setOrder(result.order);
            } else {
              // Fallback: refetch order details
              fetchOrder();
            }
            setPaying(false);
          } catch (error) {
            toast({
              title: 'Payment Verification Failed',
              description: error.response?.data?.error || 'Payment could not be verified',
              status: 'error',
              duration: 5000,
              isClosable: true
            });
            setPaying(false);
          }
        },
        prefill: {
          email: order.userEmail || '',
          contact: order.userProfile?.phoneNumber || ''
        },
        theme: {
          color: '#000000'
        },
        modal: {
          ondismiss: function() {
            setPaying(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
      razorpay.on('payment.failed', function (response) {
        toast({
          title: 'Payment Failed',
          description: response.error.description,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setPaying(false);
      });

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || error.response?.data?.error || 'Failed to initiate payment',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      setPaying(false);
    }
  };

  const canCancel = order && ['pending', 'processing', 'paid'].includes(order.status);
  const isPending = order && order.status === 'pending';

  // Timeline Component
  const OrderTimeline = ({ status }) => {
    const timelineSteps = [
      { key: 'paid', label: 'Ordered (Paid)', icon: '✓' },
      { key: 'shipped', label: 'Shipped', icon: '✓' },
      { key: 'out_for_delivery', label: 'Out for Delivery', icon: '✓' },
      { key: 'delivered', label: 'Delivered', icon: '✓' }
    ];

    const statusOrder = {
      'pending': 0,
      'processing': 0,
      'paid': 1,
      'shipped': 2,
      'out_for_delivery': 3,
      'delivered': 4,
      'cancelled': 0,
      'failed': 0
    };

    const currentStep = statusOrder[status] || 0;

    return (
      <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md" mb={6}>
        <Heading as="h2" size="lg" mb={6} fontFamily="'Cormorant', serif" fontWeight="700">
          Order Status Timeline
        </Heading>
        
        {/* Horizontal Timeline for all screen sizes - no wrapping for professional look */}
        <Flex
          justify="space-between"
          align="flex-start"
          position="relative"
          overflowX="auto"
          pb={2}
        >
          {timelineSteps.map((step, index) => {
            const isActive = index < currentStep;
            const isCurrent = index === currentStep - 1;
            const isLast = index === timelineSteps.length - 1;
            
            return (
              <React.Fragment key={step.key}>
                {/* Timeline Step */}
                <Flex direction="column" align="center" flex="1" minW={{ base: '70px', md: '90px' }}>
                  <Circle
                    size={{ base: "40px", md: "48px" }}
                    bg={isActive ? 'green.500' : isCurrent ? 'green.300' : 'gray.200'}
                    color="white"
                    fontWeight="bold"
                    fontSize={{ base: "md", md: "lg" }}
                    border="4px solid"
                    borderColor={isActive ? 'green.600' : 'white'}
                    boxShadow={isActive || isCurrent ? 'lg' : 'md'}
                    transition="all 0.3s ease"
                    zIndex={2}
                    position="relative"
                  >
                    {step.icon}
                  </Circle>
                  <Text
                    fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }}
                    fontWeight={isActive || isCurrent ? '600' : '500'}
                    color={isActive || isCurrent ? 'green.600' : 'gray.600'}
                    textAlign="center"
                    mt={2}
                    px={1}
                    lineHeight="short"
                    fontFamily="'Zain', sans-serif"
                  >
                    {step.label}
                  </Text>
                </Flex>
                
                {/* Connector Line - positioned absolutely to connect circles */}
                {!isLast && (
                  <Box
                    position="absolute"
                    top={{ base: "20px", md: "24px" }}
                    left={`${(index + 0.5) * (100 / timelineSteps.length)}%`}
                    width={`${100 / timelineSteps.length}%`}
                    h="4px"
                    bg={isActive && index < currentStep - 1 ? 'green.500' : 'gray.200'}
                    transition="all 0.3s ease"
                    zIndex={1}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Flex>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={10}>
        <Loader minH="400px" />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container maxW="container.xl" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error || 'Order not found'}
        </Alert>
        <Button mt={4} onClick={() => navigate('/orders')} variant="outline">
          View All Orders
        </Button>
      </Container>
    );
  }

  return (
    <Box bgImage={`url(${process.env.REACT_APP_BG_URL})`} minH="100vh" py={{ base: 8, md: 12 }}>
      <Container maxW="container.xl">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8} flexWrap="wrap" gap={4}>
            <Box>
              <Heading as="h1" size="2xl" mb={2} fontFamily="'Cormorant', serif" fontWeight="700">
                Order Details
              </Heading>
              <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
                Order ID: <Text as="span" fontWeight="700" fontFamily="monospace">{order.orderId.substring(0, 13)}</Text>
              </Text>
            </Box>
            <Badge
              colorScheme={getStatusColor(order.status)}
              fontSize="lg"
              px={4}
              py={2}
              borderRadius="full"
              textTransform="uppercase"
            >
              {order.status}
            </Badge>
          </Flex>

          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={8}>
            {/* Main Content */}
            <GridItem>
              <VStack spacing={6} align="stretch">
                {/* Order Timeline - Show only if not pending/cancelled/failed */}
                {!['pending', 'cancelled', 'failed'].includes(order.status) && (
                  <OrderTimeline status={order.status} />
                )}
                {/* Order Items */}
                <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md">
                  <Heading as="h2" size="lg" mb={4} fontFamily="'Cormorant', serif" fontWeight="700">
                    Order Items ({order.totalItems})
                  </Heading>
                  <VStack spacing={4} align="stretch">
                    {order.items.map((item) => (
                      <Flex
                        key={item.id}
                        gap={4}
                        p={4}
                        bg="gray.50"
                        borderRadius="md"
                        align="center"
                        flexWrap={{ base: 'wrap', sm: 'nowrap' }}
                      >
                        <Image
                          src={item.productImage || '/no_image_available.jpg'}
                          alt={item.productName}
                          boxSize={{ base: '80px', md: '100px' }}
                          objectFit="cover"
                          borderRadius="md"
                        />
                        <VStack align="start" flex={1} spacing={1}>
                          <Text fontWeight="700" fontSize={{ base: 'md', md: 'lg' }} fontFamily="'Cormorant', serif">
                            {item.productName}
                          </Text>
                          {item.productDescription && (
                            <Text fontSize="sm" color="gray.600" noOfLines={2} fontFamily="'Zain', sans-serif" fontWeight="500">
                              {item.productDescription}
                            </Text>
                          )}
                          {item.size && (
                            <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
                              Size: <Text as="span" fontWeight="600">{item.size}</Text>
                            </Text>
                          )}
                          <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
                            Quantity: <Text as="span" fontWeight="600">{item.quantity}</Text>
                          </Text>
                          <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">
                            Price: Rs {item.price}
                          </Text>
                        </VStack>
                        <Text fontWeight="700" fontSize="lg">
                          Rs {item.subtotal}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                {/* Delivery Address */}
                <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md">
                  <Heading as="h2" size="lg" mb={4} fontFamily="'Cormorant', serif" fontWeight="700">
                    Delivery Address
                  </Heading>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="700" fontSize="lg" fontFamily="'Cormorant', serif">
                      {order.userProfile.deliveringTo}
                    </Text>
                    <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">
                      {order.userProfile.addressLine1}
                    </Text>
                    {order.userProfile.addressLine2 && (
                      <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">{order.userProfile.addressLine2}</Text>
                    )}
                    {order.userProfile.landmark && (
                      <Text color="gray.600" fontSize="sm" fontFamily="'Zain', sans-serif" fontWeight="500">
                        Landmark: {order.userProfile.landmark}
                      </Text>
                    )}
                    <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">
                      {order.userProfile.locality}, {order.userProfile.city}
                    </Text>
                    <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">
                      {order.userProfile.state} - {order.userProfile.pincode}
                    </Text>
                    <HStack mt={2}>
                      <PhoneIcon color="gray.600" />
                      <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">+91 {order.userProfile.phoneNumber}</Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Order Notes */}
                {order.orderNotes && (
                  <Box bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="md">
                    <Heading as="h2" size="lg" mb={4} fontFamily="'Cormorant', serif" fontWeight="700">
                      Order Notes
                    </Heading>
                    <Text color="gray.700" fontFamily="'Zain', sans-serif" fontWeight="500">{order.orderNotes}</Text>
                  </Box>
                )}
              </VStack>
            </GridItem>

            {/* Sidebar */}
            <GridItem>
              <VStack spacing={6} align="stretch" position={{ lg: 'sticky' }} top={{ lg: 24 }}>
                {/* Order Summary */}
                <Box bg="white" p={{ base: 5, md: 6 }} borderRadius="xl" boxShadow="md">
                  <Heading as="h2" size="md" mb={4}>
                    Order Summary
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                      <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">Order Date</Text>
                      <Text fontWeight="600" fontFamily="'Zain', sans-serif">
                        {new Date(order.createdAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Text>
                    </HStack>
                    {order.paidAt && (
                      <HStack justify="space-between">
                        <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">Paid At</Text>
                        <Text fontWeight="600" fontFamily="'Zain', sans-serif">
                          {new Date(order.paidAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Text>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500">Payment Method</Text>
                      <Text fontWeight="600" textTransform="uppercase" fontFamily="'Zain', sans-serif">
                        {order.paymentMethod}
                      </Text>
                    </HStack>
                    <Divider />
                    <HStack justify="space-between">
                      <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="600">Subtotal</Text>
                      <Text fontWeight="700">Rs {order.originalAmount || order.amount}</Text>
                    </HStack>
                    {(order.couponCode || order.coupon_code) && (
                      <HStack justify="space-between" color="green.600">
                        <VStack align="start" spacing={0}>
                          <Text fontFamily="'Zain', sans-serif" fontWeight="600">Coupon Discount</Text>
                          <Text fontSize="xs" color="gray.600" fontFamily="monospace">
                            {order.couponCode || order.coupon_code} ({order.couponDiscountPercentage || order.coupon_discount_percentage}% off)
                          </Text>
                        </VStack>
                        <Text fontWeight="700">-Rs {order.discountAmount || order.discount_amount || 0}</Text>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="600">Shipping</Text>
                      <Text color="green.600" fontWeight="700">Free</Text>
                    </HStack>
                    <Divider />
                    <HStack justify="space-between">
                      <Text fontWeight="700" fontSize="lg" fontFamily="'Cormorant', serif">Total</Text>
                      <VStack align="end" spacing={0}>
                        {(order.couponCode || order.coupon_code) && order.originalAmount && (
                          <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                            Rs {order.originalAmount}
                          </Text>
                        )}
                        <Text fontWeight="700" fontSize="xl" color={(order.couponCode || order.coupon_code) ? "green.600" : "black"}>
                          Rs {order.amount}
                        </Text>
                      </VStack>
                    </HStack>
                    {(order.couponCode || order.coupon_code) && (
                      <Box bg="green.50" p={2} borderRadius="md" textAlign="center">
                        <Text fontSize="sm" color="green.700" fontWeight="600" fontFamily="'Zain', sans-serif">
                          🎉 You saved Rs {order.discountAmount || order.discount_amount || 0}!
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Box>

                {/* Actions */}
                <VStack spacing={3} align="stretch">
                  {isPending && (
                    <MotionButton
                      colorScheme="green"
                      bg="green.500"
                      color="white"
                      _hover={{ bg: 'green.600' }}
                      size="lg"
                      onClick={handlePayNow}
                      isLoading={paying}
                      loadingText="Processing..."
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                    >
                      Pay Now
                    </MotionButton>
                  )}
                  {canCancel && (
                    <MotionButton
                      variant="outline"
                      colorScheme="red"
                      size="lg"
                      onClick={handleCancelOrder}
                      isLoading={cancelling}
                      loadingText="Cancelling..."
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                    >
                      Cancel Order
                    </MotionButton>
                  )}
                  <Button
                    bg="black"
                    color="white"
                    size="lg"
                    onClick={() => navigate('/orders')}
                    _hover={{ bg: 'gray.800' }}
                    _active={{ bg: 'gray.900' }}
                    fontFamily="'Zain', sans-serif"
                    fontWeight="600"
                  >
                    View All Orders
                  </Button>
                </VStack>
              </VStack>
            </GridItem>
          </Grid>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default OrderDetailsPage;
